import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { authApi as defaultAuthApi } from './auth-api';

const AuthContext = createContext(null);

/**
 * Reads the session context. Throws outside `<AuthProvider>` instead of
 * silently returning `undefined`, so a page wired into the wrong tree fails
 * loudly during development rather than crashing later on `user.role`.
 */
export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth debe usarse dentro de <AuthProvider>.');
  return context;
}

/**
 * Session context for the whole authenticated app (spec: "Inicio y
 * recuperación de sesión"). Holds only `user` in memory — never a token,
 * cookie or password — and exposes the three states a router needs to
 * decide what to render: `checking` | `anonymous` | `authenticated`.
 *
 * `api` defaults to the real `authApi` and is only overridden by tests, so
 * production code never has to pass it explicitly.
 */
export function AuthProvider({ api = defaultAuthApi, children }) {
  const [status, setStatus] = useState('checking');
  const [user, setUser] = useState(null);

  useEffect(() => {
    let active = true;
    api.me()
      .then((data) => {
        if (!active) return;
        setUser(data.user);
        setStatus('authenticated');
      })
      .catch(() => {
        if (!active) return;
        setUser(null);
        setStatus('anonymous');
      });
    return () => {
      active = false;
    };
    // Bootstraps exactly once per provider instance (spec: "Al montar la
    // aplicación se consulta /auth/me"); `api` is a stable dependency (the
    // real module, or a fixed test double), never re-created per render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const login = useCallback(async (email, password) => {
    const data = await api.login(email, password);
    setUser(data.user);
    setStatus('authenticated');
    return data.user;
  }, [api]);

  // The cookie may still be valid server-side even when this request fails
  // to complete (network drop, 5xx): only a definitive 204 (apiRequest
  // resolves that with `null`, never any other value) or a 401 (the server
  // already considers the session gone) clear the client state. Anything
  // else — including a resolve with a non-null body, which is not the
  // contract this endpoint promises — keeps `user` untouched and throws so
  // the caller can offer a retry instead of silently pretending logout
  // succeeded.
  const logout = useCallback(async () => {
    let result;
    try {
      result = await api.logout();
    } catch (error) {
      if (error && error.status === 401) {
        setUser(null);
        setStatus('anonymous');
        return;
      }
      throw error;
    }
    if (result === null) {
      setUser(null);
      setStatus('anonymous');
      return;
    }
    throw new Error('No se pudo confirmar el cierre de sesión. Probá de nuevo.');
  }, [api]);

  // A 401 here means the session itself is already gone server-side — most
  // notably an admin resetting this same account's password from another
  // session mid-flow (spec: "Ante un 401 posterior ... el contexto borra el
  // usuario ... y vuelve al login"). Any other failure (wrong current
  // password, validation, network) leaves the session untouched so the page
  // can show the error and let the user retry the change.
  const changePassword = useCallback(async (currentPassword, newPassword) => {
    try {
      const data = await api.changePassword(currentPassword, newPassword);
      setUser(data.user);
      setStatus('authenticated');
      return data.user;
    } catch (error) {
      if (error && error.status === 401) {
        setUser(null);
        setStatus('anonymous');
      }
      throw error;
    }
  }, [api]);

  const refreshSession = useCallback(async () => {
    try {
      const data = await api.me();
      setUser(data.user);
      setStatus('authenticated');
      return data.user;
    } catch (error) {
      if (error && error.status === 401) {
        setUser(null);
        setStatus('anonymous');
      }
      throw error;
    }
  }, [api]);

  // Central place pages call when an API request fails, so the rule "a 401
  // clears the session, a pending password change wins over the current
  // screen" lives once instead of being re-implemented per page. Anything
  // else (a plain 403, a validation error, ...) is left `handled: false` so
  // the caller shows its own message; the session is never touched for
  // those (spec: "Los demás 403 conservan la sesión").
  const handleApiError = useCallback((error, { clearBusinessData } = {}) => {
    if (error && error.status === 401) {
      setUser(null);
      setStatus('anonymous');
      if (typeof clearBusinessData === 'function') clearBusinessData();
      return { handled: true, reason: 'session_expired' };
    }
    if (error && error.code === 'password_change_required') {
      setUser((current) => (current ? { ...current, mustChangePassword: true } : current));
      if (typeof clearBusinessData === 'function') clearBusinessData();
      return { handled: true, reason: 'password_change_required' };
    }
    return { handled: false };
  }, []);

  if (status === 'checking') {
    return (
      <div role="status" aria-label="Cargando sesión" className="gym-boot">
        <span className="gym-boot__spinner" aria-hidden="true" />
        Cargando sesión…
      </div>
    );
  }

  const value = { status, user, login, logout, changePassword, refreshSession, handleApiError };
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export { AuthContext };
